"use client";

import { useState } from "react";
import {
  Settings,
  Plus,
  GripVertical,
  Pencil,
  Trash2,
  X,
  Check,
  RotateCcw,
} from "lucide-react";

import { Button } from "@/app/_module/components/ui/button";
import { Input } from "@/app/_module/components/ui/input";
import { Label } from "@/app/_module/components/ui/label";
import {
  Dialog,
  ResponsiveDialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/app/_module/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  ResponsiveAlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/app/_module/components/ui/alert-dialog";

import { PIPELINE_STAGE_ICONS, pipelineStageIcon } from "../constants/pipeline-stage-icons";
import type { PipelineStage } from "../types/pipeline-stage.types";
import {
  useAllPipelineStages,
  useCreatePipelineStage,
  useUpdatePipelineStage,
  useTogglePipelineStageVisibility,
  useDeletePipelineStage,
  useReorderPipelineStages,
  useResetPipelineStageOrder,
  usePipelineStageImpact,
} from "../hooks/queries";

// Preset color swatches (quick picks)
const PRESET_COLORS = [
  "#3b82f6", "#0891b2", "#f59e0b", "#059669", "#ef4444",
  "#dc2626", "#92400e", "#6b7280", "#111827", "#8b5cf6",
  "#ec4899", "#14b8a6", "#f97316", "#6366f1", "#84cc16",
];

// Color picker component — presets + native hex picker
function ColorPicker({ value, onChange }: { value: string; onChange: (color: string) => void }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs text-muted-foreground">Color</Label>
      <div className="flex items-center gap-2">
        <div className="flex gap-1 flex-wrap flex-1">
          {PRESET_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => onChange(c)}
              className={`w-5 h-5 rounded-full border-2 transition-all ${
                value === c ? "border-gray-900 scale-110" : "border-transparent hover:border-gray-300"
              }`}
              style={{ backgroundColor: c }}
            />
          ))}
        </div>
        {/* Native color picker for any hex color */}
        <div className="relative flex-shrink-0">
          <input
            type="color"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-8 h-8 rounded-full cursor-pointer border-2 border-gray-200 p-0 appearance-none [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:rounded-full [&::-webkit-color-swatch]:border-none [&::-moz-color-swatch]:rounded-full [&::-moz-color-swatch]:border-none overflow-hidden"
          />
        </div>
      </div>
    </div>
  );
}

// Icon picker component
function IconPicker({ value, onChange }: { value?: string; onChange: (icon: string) => void }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs text-muted-foreground">Icon</Label>
      <div className="flex gap-1 flex-wrap">
        {PIPELINE_STAGE_ICONS.map(({ name, icon: IconComp }) => (
          <button
            key={name}
            type="button"
            onClick={() => onChange(name)}
            className={`w-7 h-7 rounded-md flex items-center justify-center transition-all ${
              value === name
                ? "bg-blue-100 border-2 border-blue-500 text-blue-700"
                : "border border-gray-200 text-gray-500 hover:border-gray-400 hover:text-gray-700"
            }`}
          >
            <IconComp className="h-3.5 w-3.5" />
          </button>
        ))}
      </div>
    </div>
  );
}

interface PipelineStageManagerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const PipelineStageManager = function ({ open, onOpenChange }: PipelineStageManagerProps) {
  const { data: stages, isLoading } = useAllPipelineStages();
  const createStage = useCreatePipelineStage();
  const updateStage = useUpdatePipelineStage();
  const toggleVisibility = useTogglePipelineStageVisibility();
  const deleteStage = useDeletePipelineStage();
  const reorderStages = useReorderPipelineStages();
  const resetOrder = useResetPipelineStageOrder();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState("#3b82f6");
  const [newIcon, setNewIcon] = useState("ClipboardList");

  const [editingStage, setEditingStage] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editColor, setEditColor] = useState("");
  const [editIcon, setEditIcon] = useState("");

  const [deleteConfirm, setDeleteConfirm] = useState<PipelineStage | null>(null);
  // Two-step delete: step 1 shows what the stage holds, step 2 is the point of
  // no return. Born from a real ticket — a whole pipeline deleted on one click.
  const [deleteStep, setDeleteStep] = useState<1 | 2>(1);
  const { data: deleteImpact } = usePipelineStageImpact(deleteConfirm?.id ?? null);

  // Drag state — local order for optimistic UI
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [localOrder, setLocalOrder] = useState<PipelineStage[] | null>(null);

  // Use localOrder during drag, otherwise use server data
  const displayStages = localOrder || stages;

  // Custom-stage cap (must match backend MAX_CUSTOM_STAGES). Surface it up front
  // so users know they're at the limit before trying to add (and so importing to
  // a maxed-out pipeline isn't a silent surprise).
  const MAX_CUSTOM_STAGES = 100;
  const customStageCount = (stages || []).filter((s) => !s.isSystem).length;
  const atCustomMax = customStageCount >= MAX_CUSTOM_STAGES;

  const handleCreate = () => {
    if (!newName.trim()) return;
    createStage.mutate(
      { name: newName.trim(), color: newColor, icon: newIcon },
      {
        onSuccess: () => {
          setNewName("");
          setNewColor("#3b82f6");
          setNewIcon("ClipboardList");
          setShowCreateForm(false);
        },
      }
    );
  };

  const handleUpdate = (stageId: string) => {
    const data: { name?: string; color?: string; icon?: string } = {};
    const stage = stages?.find((s) => s.id === stageId);
    if (!stage) return;

    // Stage names are immutable once created — prevents lead_stat orphaning
    if (editColor && editColor !== stage.color) data.color = editColor;
    if (editIcon && editIcon !== (stage.icon || "")) data.icon = editIcon;

    if (Object.keys(data).length === 0) {
      setEditingStage(null);
      return;
    }

    updateStage.mutate(
      { id: stageId, data },
      { onSuccess: () => setEditingStage(null) }
    );
  };

  const startEdit = (stage: PipelineStage) => {
    setEditingStage(stage.id);
    setEditName(stage.name);
    setEditColor(stage.color);
    setEditIcon(stage.icon || "");
  };

  const handleDragStart = (stageId: string) => {
    setDraggedId(stageId);
    // Initialize local order from current stages
    if (stages) setLocalOrder([...stages]);
  };

  // Shared reorder core — used by mouse drag-over AND touch move.
  const reorderTo = (targetId: string) => {
    if (!draggedId || draggedId === targetId) return;

    const source = localOrder || stages;
    if (!source) return;

    const currentOrder = source.map((s) => s.id);
    const dragIndex = currentOrder.indexOf(draggedId);
    const targetIndex = currentOrder.indexOf(targetId);

    if (dragIndex === -1 || targetIndex === -1) return;

    // Optimistically reorder the local list
    const reordered = [...source];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(targetIndex, 0, moved);
    setLocalOrder(reordered);
  };

  const handleDragOver = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    reorderTo(targetId);
  };

  // Touch reordering (HTML5 drag-and-drop does not exist on iOS): grab the
  // handle, slide your finger — the row under the finger becomes the target.
  const handleTouchMove = (e: React.TouchEvent) => {
    if (!draggedId) return;
    const t = e.touches[0];
    const el = document.elementFromPoint(t.clientX, t.clientY);
    const row = el?.closest("[data-stage-id]") as HTMLElement | null;
    const targetId = row?.dataset.stageId;
    if (targetId) reorderTo(targetId);
  };

  const handleDragEnd = () => {
    // Persist the final order to the server
    if (localOrder) {
      reorderStages.mutate(localOrder.map((s) => s.id));
    }
    setDraggedId(null);
    // Keep localOrder visible until server refetch completes
    // It will be cleared when stages data updates
    setTimeout(() => setLocalOrder(null), 500);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <ResponsiveDialogContent size="xl" className="max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Manage Pipeline Stages
            </DialogTitle>
            <DialogDescription>
              Customize your lead pipeline. Drag to reorder or create custom stages.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-2 py-2">
            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">Loading stages...</div>
            ) : (
              displayStages?.map((stage) => {
                const isEditing = editingStage === stage.id;
                const isDragging = draggedId === stage.id;
                const StageIcon = pipelineStageIcon(stage.icon);

                return (
                  <div key={stage.id}>
                    <div
                      data-stage-id={stage.id}
                      draggable
                      onDragStart={() => handleDragStart(stage.id)}
                      onDragOver={(e) => handleDragOver(e, stage.id)}
                      onDragEnd={handleDragEnd}
                      className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                        isDragging ? "opacity-50 border-dashed" : "border-gray-200"
                      } ${!stage.isVisible ? "opacity-60" : ""}`}
                    >
                      {/* Drag handle — touch-none so grabbing it drags the
                          row instead of scrolling the list on iOS */}
                      <div
                        className="cursor-grab touch-none p-1 -m-1 active:cursor-grabbing text-gray-400 hover:text-gray-600"
                        onTouchStart={() => handleDragStart(stage.id)}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleDragEnd}
                      >
                        <GripVertical className="h-4 w-4" />
                      </div>

                      {/* Color swatch + icon */}
                      <div
                        className="w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center"
                        style={{ backgroundColor: stage.color }}
                      >
                        <StageIcon className="h-4 w-4 text-white" />
                      </div>

                      {/* Name */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium truncate">{stage.name}</span>
                          {stage.isSystem && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">
                              System
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {isEditing ? (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0"
                              onClick={() => handleUpdate(stage.id)}
                              disabled={updateStage.isPending}
                            >
                              <Check className="h-3.5 w-3.5 text-green-600" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0"
                              onClick={() => setEditingStage(null)}
                            >
                              <X className="h-3.5 w-3.5 text-gray-500" />
                            </Button>
                          </>
                        ) : (
                          <>
                            {/* Only custom stages can be edited */}
                            {!stage.isSystem && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0"
                                onClick={() => startEdit(stage)}
                              >
                                <Pencil className="h-3.5 w-3.5 text-gray-500" />
                              </Button>
                            )}
                            {/* Only custom stages can be deleted */}
                            {!stage.isSystem && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0"
                                onClick={() => setDeleteConfirm(stage)}
                              >
                                <Trash2 className="h-3.5 w-3.5 text-red-500" />
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {/* Expanded edit form below the row */}
                    {isEditing && (
                      <div className="ml-7 mr-3 mt-1 mb-2 p-3 rounded-lg bg-muted/50 border border-gray-100 space-y-3">
                        <p className="text-xs text-muted-foreground">
                          Stage names cannot be changed after creation. You can update the color and icon below.
                        </p>
                        <ColorPicker value={editColor} onChange={setEditColor} />
                        <IconPicker value={editIcon} onChange={setEditIcon} />
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Create new stage form */}
          {showCreateForm ? (
            <div className="border-t pt-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">New Pipeline Stage</Label>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 w-7 p-0"
                  onClick={() => {
                    setShowCreateForm(false);
                    setNewName("");
                    setNewColor("#3b82f6");
                    setNewIcon("ClipboardList");
                  }}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
              <div className="flex gap-3">
                <Input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Stage name"
                  className="flex-1 h-9"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleCreate();
                  }}
                />
                <Button
                  size="sm"
                  onClick={handleCreate}
                  disabled={!newName.trim() || createStage.isPending}
                  className="h-9"
                >
                  {createStage.isPending ? "Creating..." : "Add"}
                </Button>
              </div>
              <ColorPicker value={newColor} onChange={setNewColor} />
              <IconPicker value={newIcon} onChange={setNewIcon} />
            </div>
          ) : (
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-2">
                <span className={`text-xs ${atCustomMax ? "font-medium text-amber-600" : "text-muted-foreground"}`}>
                  {customStageCount} / {MAX_CUSTOM_STAGES} custom stages used
                  {atCustomMax && " — maximum reached"}
                </span>
              </div>
              <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowCreateForm(true)}
                disabled={atCustomMax}
                title={atCustomMax ? `You've reached the maximum of ${MAX_CUSTOM_STAGES} custom pipeline stages` : undefined}
              >
                <Plus className="h-4 w-4 mr-2" />
                {atCustomMax ? `Maximum reached (${MAX_CUSTOM_STAGES})` : "Add Custom Stage"}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground"
                onClick={() => setShowResetConfirm(true)}
                title="Reset to default order, colors, and visibility"
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
              </div>
            </div>
          )}
        </ResponsiveDialogContent>
      </Dialog>

      {/* Delete confirmation - only for custom stages. TWO steps: the first
          shows exactly what the column holds (a whole pipeline was once deleted
          on a single click), the second is the point of no return. */}
      <AlertDialog
        open={!!deleteConfirm}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteConfirm(null);
            setDeleteStep(1);
          }
        }}
      >
        <ResponsiveAlertDialogContent size="md">
          {deleteStep === 1 ? (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete &ldquo;{deleteConfirm?.name}&rdquo;?</AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-2">
                    {deleteImpact && (deleteImpact.totalLeads ?? 0) > 0 ? (
                      <p>
                        This pipeline currently holds{" "}
                        <span className="font-semibold text-foreground">
                          {Number(deleteImpact.totalLeads).toLocaleString()} lead{deleteImpact.totalLeads === 1 ? "" : "s"}
                        </span>
                        {(deleteImpact.ownedLeads ?? 0) > 0 && (
                          <> — {Number(deleteImpact.ownedLeads).toLocaleString()} of them move to &ldquo;Leads&rdquo;</>
                        )}
                        . The column disappears from your pipeline view.
                      </p>
                    ) : (
                      <p>This pipeline is empty — deleting it just removes the column.</p>
                    )}
                    <p className="text-xs">
                      You can undo this from Recently Deleted on the Leads page — restoring
                      brings the column back <em>and</em> returns its leads.
                    </p>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <Button
                  variant="destructive"
                  onClick={() => setDeleteStep(2)}
                >
                  Continue
                </Button>
              </AlertDialogFooter>
            </>
          ) : (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Are you sure you want to delete &ldquo;{deleteConfirm?.name}&rdquo;?</AlertDialogTitle>
                <AlertDialogDescription>
                  {deleteImpact && (deleteImpact.totalLeads ?? 0) > 0
                    ? `${Number(deleteImpact.totalLeads).toLocaleString()} lead${deleteImpact.totalLeads === 1 ? "" : "s"} will lose this column until the pipeline is restored.`
                    : "This removes the pipeline column from your view."}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-red-600 hover:bg-red-700"
                  onClick={() => {
                    if (deleteConfirm) {
                      deleteStage.mutate(deleteConfirm.id);
                      setDeleteConfirm(null);
                      setDeleteStep(1);
                    }
                  }}
                >
                  Yes, delete pipeline
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </ResponsiveAlertDialogContent>
      </AlertDialog>

      {/* Reset confirmation */}
      <AlertDialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <ResponsiveAlertDialogContent size="md">
          <AlertDialogHeader>
            <AlertDialogTitle>Reset to defaults?</AlertDialogTitle>
            <AlertDialogDescription>
              This will restore the original order, colors, and icons for system stages, show all hidden stages, and move custom stages to the end alphabetically.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                resetOrder.mutate();
                setShowResetConfirm(false);
              }}
            >
              Reset
            </AlertDialogAction>
          </AlertDialogFooter>
        </ResponsiveAlertDialogContent>
      </AlertDialog>
    </>
  );
};
